import { useMemo, useState } from "react";
import { mfgCompactGhostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import { uniqueFacetLabels } from "@/lib/manufacturing/pricingCatalogFacets";
import {
  filterProductScopeRows,
  productScopePool,
  widthsInRows,
  type ProductScopeRow,
} from "@/lib/manufacturing/cogsColumnProductScope";
import { PricingFacetMultiSelect } from "./PricingFacetMultiSelect";

type Props = {
  rows: readonly ProductScopeRow[];
  productsOnly: boolean;
  enabled: boolean;
  selectedIds: readonly string[];
  locked: boolean;
  onEnabled: (enabled: boolean) => void;
  onSelectedIds: (ids: string[]) => void;
};

export function PricingColumnProductScope({
  rows,
  productsOnly,
  enabled,
  selectedIds,
  locked,
  onEnabled,
  onSelectedIds,
}: Props) {
  const [groups, setGroups] = useState<string[]>([]);
  const [styles, setStyles] = useState<string[]>([]);
  const [sizes, setSizes] = useState<string[]>([]);
  const [widths, setWidths] = useState<string[]>([]);
  const pool = useMemo(() => productScopePool(rows, productsOnly), [rows, productsOnly]);
  const shown = useMemo(
    () => filterProductScopeRows(pool, { groups, styles, sizes, widths }),
    [pool, groups, styles, sizes, widths],
  );
  const selected = useMemo(() => new Set(selectedIds.map((id) => id.toLowerCase())), [selectedIds]);
  const groupOptions = useMemo(() => uniqueFacetLabels(pool.map((row) => row.series)), [pool]);
  const styleOptions = useMemo(() => uniqueFacetLabels(pool.map((row) => row.roof_style)), [pool]);
  const sizeOptions = useMemo(() => uniqueFacetLabels(pool.map((row) => row.size_band)), [pool]);
  const widthOptions = useMemo(
    () => widthsInRows(pool).map((width) => ({ id: width, label: `${width} wide` })),
    [pool],
  );

  function toggle(id: string) {
    const key = id.toLowerCase();
    if (selected.has(key)) onSelectedIds(selectedIds.filter((item) => item.toLowerCase() !== key));
    else onSelectedIds([...selectedIds, id]);
  }

  function selectShown() {
    const next = new Map(selectedIds.map((id) => [id.toLowerCase(), id]));
    for (const row of shown) next.set(row.subject_id.toLowerCase(), row.subject_id);
    onSelectedIds([...next.values()]);
  }

  function clearShown() {
    const hide = new Set(shown.map((row) => row.subject_id.toLowerCase()));
    onSelectedIds(selectedIds.filter((id) => !hide.has(id.toLowerCase())));
  }

  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-zinc-300">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(event) => onEnabled(event.target.checked)}
          disabled={locked}
        />
        Only certain products
      </label>
      {enabled ? (
        <div className="space-y-2 rounded-lg border border-gray-200 p-2 dark:border-zinc-800">
          <p className="text-xs text-gray-600 dark:text-zinc-400">
            Filter, then choose the products that get this amount. Every other product is $0.
            {selectedIds.length === 1 ? " 1 product selected." : ` ${selectedIds.length} products selected.`}
          </p>
          <div className="flex flex-wrap items-end gap-2">
            <PricingFacetMultiSelect
              label="Group"
              options={groupOptions}
              selected={groups}
              onChange={setGroups}
              disabled={locked}
            />
            <PricingFacetMultiSelect
              label="Style"
              options={styleOptions}
              selected={styles}
              onChange={setStyles}
              disabled={locked}
            />
            <PricingFacetMultiSelect
              label="Width"
              options={widthOptions.map((item) => item.label)}
              selected={widths.map((width) => `${width} wide`)}
              onChange={(next) =>
                setWidths(
                  next
                    .map((label) => widthOptions.find((item) => item.label === label)?.id ?? "")
                    .filter(Boolean),
                )
              }
              disabled={locked}
            />
            <PricingFacetMultiSelect
              label="Size"
              options={sizeOptions}
              selected={sizes}
              onChange={setSizes}
              disabled={locked}
            />
            <button type="button" className={mfgCompactGhostBtnClass} disabled={locked || shown.length === 0} onClick={selectShown}>
              Select shown
            </button>
            <button type="button" className={mfgCompactGhostBtnClass} disabled={locked || shown.length === 0} onClick={clearShown}>
              Clear shown
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto rounded border border-gray-200 dark:border-zinc-800">
            {pool.length === 0 ? (
              <p className="px-2 py-2 text-xs text-gray-500 dark:text-zinc-400">The product list is still loading.</p>
            ) : shown.length === 0 ? (
              <p className="px-2 py-2 text-xs text-gray-500 dark:text-zinc-400">No products match these filters.</p>
            ) : (
              shown.map((row) => (
                <label
                  key={row.subject_id}
                  className="flex items-center gap-2 border-b border-gray-100 px-2 py-1 text-xs last:border-b-0 dark:border-zinc-900"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(row.subject_id.toLowerCase())}
                    onChange={() => toggle(row.subject_id)}
                    disabled={locked}
                  />
                  <span className="min-w-0 flex-1 truncate">{row.name}</span>
                  <span className="shrink-0 text-gray-500 dark:text-zinc-400">{row.size_band}</span>
                </label>
              ))
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
